import { useTheme } from "../context/ThemeContext";
import { Moon, Sun } from "lucide-react";
import { lagosHour } from "../utils/tripLog";

function Topbar() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  // Theme variables
  const bgColor = isDark ? 'bg-gray-800' : 'bg-white';
  const textColor = isDark ? 'text-white' : 'text-gray-800';
  const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
  const textMuted = isDark ? 'text-gray-200' : 'text-gray-700';
  const inputBg = isDark ? 'bg-gray-700' : 'bg-white';
  const inputBorder = isDark ? 'border-gray-600' : 'border-orange-300';
  const inputText = isDark ? 'text-white' : 'text-gray-900';
  const inputPlaceholder = isDark ? 'placeholder-gray-400' : 'placeholder-gray-400';
  const hoverBg = isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100';
  const profileBg = isDark ? 'bg-gray-600' : 'bg-gray-200';
  const profileText = isDark ? 'text-gray-200' : 'text-gray-600';

  const getGreeting = () => {
    const hour = lagosHour();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Logged-in admin (stored at login). Show the real name + role.
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    user = {};
  }
  const displayName = user.name || user.email || 'Admin';
  const roleLabel = user.role === 'super_admin' ? 'Super Admin' : 'Admin';
  const initials = (user.name || user.email || 'A')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className={`${bgColor} p-4 shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 transition-colors`}>
      {/* Left side - Greeting */}
      <div className="flex-1">
        <h1 className={`text-2xl font-bold ${textColor}`}>
          Hello {(user.name || "Admin").split(/\s+/)[0]}
        </h1>
        <p className={`${textSecondary} text-sm`}>{getGreeting()}</p>
      </div>

      {/* Right side - Search and Profile */}
      <div className="w-full sm:w-auto flex flex-col sm:flex-row items-center gap-4">
        {/* Search Bar */}
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search..."
            className={`w-full border ${inputBorder} rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-[#FFD36980] focus:border-transparent text-sm ${inputBg} ${inputText} ${inputPlaceholder} transition-colors`}
          />
          <svg
            className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'} absolute left-3 top-1/2 transform -translate-y-1/2`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Profile */}
        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start">
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full ${hoverBg} transition-colors`}
            aria-label="Toggle theme"
          >
            {isDark ? (
              <Sun className="w-5 h-5 text-yellow-400" />
            ) : (
              <Moon className="w-5 h-5 text-gray-600" />
            )}
          </button>

          <div className="text-right sm:text-left">
            <p className={`text-sm font-medium ${textMuted}`}>{displayName}</p>
            <p className={`text-xs ${textSecondary}`}>{roleLabel}</p>
          </div>

          <div className={`w-10 h-10 rounded-full ${profileBg} flex items-center justify-center`}>
            <span className={`${profileText} font-medium`}>{initials}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Topbar;